import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useStableAvatar } from '@/hooks/use-stable-avatar';
import { Book } from '@/types/book';

export interface ContinueReadingBook extends Book {
  lastPage: number;
  lastReadAt: string;
  hasProgress: boolean;
  progressPercentage: number;
}

/**
 * Lightweight hook that fetches only recent reading sessions
 * to build the "Continue Reading" list efficiently.
 * 
 * Instead of querying progress for ALL books in the catalog,
 * this fetches only the user's recent sessions and maps to books.
 */
export function useContinueReading(allBooks: Book[], limit = 50) {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const userId = session?.user?.id;
  const profileId = currentProfile?.id;

  // Fetch only recent reading sessions (small, fast query)
  const { data: recentSessions = [], isLoading } = useQuery({
    queryKey: ['continueReadingSessions', userId, profileId],
    queryFn: async () => {
      if (!userId || !profileId) return [];

      const { data, error } = await supabase
        .from('reading_sessions')
        .select('book_id, current_page, end_time, start_time')
        .eq('user_id', userId)
        .eq('profile_id', profileId)
        .gt('current_page', 0)
        .order('end_time', { ascending: false, nullsFirst: false })
        .order('start_time', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching continue reading sessions:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!userId && !!profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes - data doesn't change that often
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on every focus - use background sync instead
    refetchOnReconnect: false,
    refetchOnMount: false, // Use cached data, refresh in background
  });

  // Build continue reading list from sessions
  const continueReadingBooks = useMemo(() => {
    if (!recentSessions || recentSessions.length === 0 || allBooks.length === 0) {
      return [];
    }

    // Create a map of book_id -> most recent session
    const bookSessionMap = new Map<number, { currentPage: number; lastReadAt: string }>();
    
    for (const session of recentSessions) {
      if (!bookSessionMap.has(session.book_id)) {
        bookSessionMap.set(session.book_id, {
          currentPage: session.current_page || 0,
          lastReadAt: session.end_time || session.start_time,
        });
      }
    }

    // Create a book lookup map for O(1) access
    const bookMap = new Map(allBooks.map(book => [Number(book.id), book]));

    // Build the continue reading list in recency order
    const result: ContinueReadingBook[] = [];
    
    for (const [bookId, sessionData] of bookSessionMap) {
      const book = bookMap.get(bookId);
      if (!book) continue;

      // Skip upcoming/building books
      if (book.bundle?.status === 'upcoming' || book.bundle?.status === 'building') {
        continue;
      }

      // Calculate progress percentage
      const estimatedTotalPages = calculateTotalPages(book);
      const progressPercentage = Math.min((sessionData.currentPage / estimatedTotalPages) * 100, 100);

      result.push({
        ...book,
        lastPage: sessionData.currentPage,
        lastReadAt: sessionData.lastReadAt,
        hasProgress: true,
        progressPercentage,
      });
    }

    return result;
  }, [recentSessions, allBooks]);

  return {
    continueReadingBooks,
    isLoading,
    hasProgress: continueReadingBooks.length > 0,
  };
}

/**
 * Calculate total pages for a book based on its content structure
 */
function calculateTotalPages(book: Book): number {
  if (!book.description) return 100;

  const paragraphsPerPage = book.paragraphs_per_page || 3;

  const allBlocks = book.description
    .split(/\n\s*\n/)
    .filter(p => p.trim().length > 0)
    .map(p => p.trim());

  let totalPages = 0;
  let currentParagraphs = 0;

  for (const block of allBlocks) {
    const isChapterTitle =
      block.match(/^chapter\s+\d+/i) ||
      block.match(/^chapter\s+\d+\s*:/i) ||
      (block.toLowerCase().includes('chapter') && block.length < 150) ||
      (block.match(/^[A-Z][^.!?]*$/) && block.length < 100);

    if (isChapterTitle) {
      if (currentParagraphs > 0) {
        totalPages++;
        currentParagraphs = 0;
      }
      totalPages++;
    } else {
      currentParagraphs++;
      if (currentParagraphs >= paragraphsPerPage) {
        totalPages++;
        currentParagraphs = 0;
      }
    }
  }

  if (currentParagraphs > 0) {
    totalPages++;
  }

  return Math.max(totalPages, 1);
}
